import Chatbot from "react-chatbot-kit";
import "react-chatbot-kit/build/main.css";
import "./Chatbot.css";
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import config from "./config";
import MessageParser from "./MessageParser";
import ActionProvider from "./ActionProvider";

function MyChatbot() {
  const [isOpen,      setIsOpen]      = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [dimensions,  setDimensions]  = useState({ width: 380, height: 520 });
  const [isResizing,  setIsResizing]  = useState(false);

  const resizeRef   = useRef(null);
  const startPos    = useRef({ x:0, y:0, width:0, height:0 });
  const resizeDir   = useRef(null);

  const HEADER_H = 62;
  const INPUT_H  = 56;

  const winW = isMaximized ? Math.min(window.innerWidth  - 60, 900) : dimensions.width;
  const winH = isMaximized ? Math.min(window.innerHeight - 120, 700) : dimensions.height;

  /* ── fix inner kit heights whenever window size changes ── */
  useEffect(() => {
    if (!isOpen) return;
    const fix = () => {
      const msgEl   = document.querySelector(".react-chatbot-kit-chat-message-container");
      const inputEl = document.querySelector(".react-chatbot-kit-chat-input-container");
      const innerEl = document.querySelector(".react-chatbot-kit-chat-inner-container");
      const bodyH   = winH - HEADER_H;
      const msgH    = bodyH - INPUT_H;
      if (innerEl) { innerEl.style.height = bodyH + "px"; innerEl.style.maxHeight = bodyH + "px"; }
      if (msgEl)   { msgEl.style.height = msgH + "px"; msgEl.style.maxHeight = msgH + "px"; msgEl.style.overflowY = "auto"; }
      if (inputEl) { inputEl.style.position = "static"; inputEl.style.width = "100%"; }
    };
    fix();
    const t = setTimeout(fix, 100);
    return () => clearTimeout(t);
  }, [isOpen, isMaximized, dimensions]);

  /* ── resize logic (unchanged) ── */
  const handleMouseDown = (e, dir) => {
    e.preventDefault(); e.stopPropagation();
    setIsResizing(true);
    resizeDir.current = dir;
    startPos.current  = { x:e.clientX, y:e.clientY, width:dimensions.width, height:dimensions.height };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isResizing || isMaximized) return;
      const dx = startPos.current.x - e.clientX;
      const dy = startPos.current.y - e.clientY;
      const d  = resizeDir.current;
      let w = dimensions.width, h = dimensions.height;
      if (d.includes("left"))   w = Math.max(320, Math.min(900, startPos.current.width  + dx));
      if (d.includes("right"))  w = Math.max(320, Math.min(900, startPos.current.width  - dx));
      if (d.includes("top"))    h = Math.max(380, Math.min(800, startPos.current.height + dy));
      if (d.includes("bottom")) h = Math.max(380, Math.min(800, startPos.current.height - dy));
      setDimensions({ width:w, height:h });
    };
    const onUp = () => { setIsResizing(false); resizeDir.current = null; };
    if (isResizing) {
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup",   onUp);
    }
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
  }, [isResizing, dimensions, isMaximized]);

  return (
    <div style={{ position:"fixed", bottom:"24px", right:"24px", zIndex:9999 }}>

      {/* ── Floating toggle button ── */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button className="cb-toggle"
            initial={{ scale:0, opacity:0 }}
            animate={{ scale:1, opacity:1 }}
            exit={{ scale:0, opacity:0 }}
            transition={{ type:"spring", stiffness:260, damping:18 }}
            onClick={() => setIsOpen(true)}
            title="Open chat">
            <i className="bx bx-chat"></i>
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── Chat window ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={resizeRef}
            className="cb-window"
            style={{
              width:  winW,
              height: winH,
              userSelect: isResizing ? "none" : "auto",
              position: "relative",
            }}
            initial={{ opacity:0, y:20, scale:.95 }}
            animate={{ opacity:1, y:0,  scale:1 }}
            exit={{    opacity:0, y:16, scale:.95 }}
            transition={{ duration:.25, ease:[.22,.68,0,1.2] }}>

            {/* Header */}
            <div className="cb-header">
              <div className="cb-header-left">
                <span className="cb-avatar"><i className="bx bx-bot"></i></span>
                <div>
                  <p className="cb-bot-name">NMM Bot</p>
                  <p className="cb-bot-status">
                    <span className="cb-online-dot"></span> Online
                  </p>
                </div>
              </div>
              <div className="cb-header-actions">
                <button className="cb-header-btn" title={isMaximized?"Restore":"Maximize"}
                  onClick={() => setIsMaximized(p => !p)}>
                  <i className={`bx ${isMaximized?"bx-collapse":"bx-expand"}`}></i>
                </button>
                <button className="cb-header-btn" title="Close"
                  onClick={() => setIsOpen(false)}>
                  <i className="bx bx-x"></i>
                </button>
              </div>
            </div>

            {/* Chatbot body */}
            <div className="cb-body" style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
              height: "calc(100% - 62px)"
            }}>
              <Chatbot
                config={config}
                messageParser={MessageParser}
                actionProvider={ActionProvider}
              />
            </div>

            {/* Resize handles (unchanged logic) */}
            {!isMaximized && (
              <>
                {[
                  { dir:"top-left",     style:{ top:0,    left:0,    width:14, height:14, cursor:"nwse-resize" } },
                  { dir:"top-right",    style:{ top:0,    right:0,   width:14, height:14, cursor:"nesw-resize" } },
                  { dir:"bottom-left",  style:{ bottom:0, left:0,    width:14, height:14, cursor:"nesw-resize" } },
                  { dir:"bottom-right", style:{ bottom:0, right:0,   width:14, height:14, cursor:"nwse-resize" } },
                  { dir:"top",    style:{ top:0,    left:14, right:14, height:5, cursor:"ns-resize" } },
                  { dir:"bottom", style:{ bottom:0, left:14, right:14, height:5, cursor:"ns-resize" } },
                  { dir:"left",   style:{ top:14, bottom:14, left:0,  width:5, cursor:"ew-resize" } },
                  { dir:"right",  style:{ top:14, bottom:14, right:0, width:5, cursor:"ew-resize" } },
                ].map(h => (
                  <div key={h.dir}
                    onMouseDown={e => handleMouseDown(e, h.dir)}
                    style={{ position:"absolute", zIndex:10, ...h.style }} />
                ))}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MyChatbot;
